import * as React from 'react';
import { Route, Routes } from 'react-router-dom';
import { DevicesInventoryDashboard } from './devices-inventory/devices-inventory-dashboard';
import { useAuthStore, useRoutesStore, useStores } from '../../../store';
import { PermissionPage, Error404, DevicesPermissionWrappedProvider } from 'ui-lib';

import { CommissioningWizard } from './wizard';
import { hasPermission } from '../utils';
import shellPromptCss from './shell-prompt/shellprompt.css';
import { CommissioningUpload, CommissioningUploadReview } from './commissioning-upload';

import commissioningUploadCss from './commissioning-upload/commissioning-upload.css';
import { DecommissioningDevice } from './decommissioning';
import { ShellCommandExecution } from './shell-prompt/shellprompt';
import { VarModalDeviceDetails } from './devices-inventory/device-details';
import { FileTransfer } from './file-transfer/file-transfer';
import { ManageDeviceGroupsPage } from './device-groups';
import { DeviceReset } from './device-reset/device-reset';
import { WelcomePackConfigurationsListing } from './welcome-pack/welcome-pack-listing';
import { SaveWelcomePack } from './welcome-pack/save';
import { WelcomePackTracking } from './welcome-pack/welcome-pack-tracking';
import { NewFileTransfer } from './new-file-transfer/new-file-transfer';

export enum DEVICES_ROUTE_CODES {
    ROOT = 'DEVICES',
    DETAILS = 'DEVICES_DETAILS',
    INVENTORY = 'DEVICES_INVENTORY',
    COMMISSIONING = 'DEVICES_COMMISSIONING_WIZARD',
    COMMISSIONING_UPLOAD = 'DEVICES_COMMISSIONING_UPLOAD',
    COMMISSIONING_UPLOAD_REVIEW = 'DEVICES_COMMISSIONING_UPLOAD_REVIEW',
    SHELL_COMMAND = 'DEVICES_SHELL_COMMAND',
    FILE_TRANSFER = 'DEVICES_FILE_TRANSFER',
    NEW_FILE_TRANSFER = 'DEVICES_NEW_FILE_TRANSFER',
    BULK_REMOTE_COMMAND = 'DEVICES_REMOTE_COMMAND',
    DECOMMISSIONING = 'DEVICES_DECOMMISSIONING',
    COMMISSIONED = 'DEVICES_COMMISSIONED',
    DEVICE_GROUPS = 'DEVICES_DEVICE_GROUPS',
    DEVICE_RESET = 'DEVICES_DEVICE_RESET',
    WELCOME_PACKS = 'DEVICES_WELCOME_PACKS',
    WELCOME_PACK = 'DEVICES_WELCOME_PACK',
    WELCOME_PACK_TRACKING = 'DEVICES_WELCOME_PACK_TRACKING',
}

enum EXCLUDE_CODES {
    COMMISSIONED = 'commissioned',
}

export function DevicePages() {
    const { isAuthenticated } = useAuthStore();
    const routeStore = useRoutesStore();
    const stores = useStores();

    const canAccessCommissioning = React.useMemo(
        () => isAuthenticated && hasPermission(DEVICES_ROUTE_CODES.COMMISSIONING, stores),
        [],
    );

    const rootRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.ROOT);

    return (
        <Routes>
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.INVENTORY).trimRootPath(rootRoute.path)}
                element={
                    <DevicesPermissionWrappedProvider
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='Inventory'
                        requiredPermissionCodes={['Device Inventory']}
                    >
                        <DevicesInventoryDashboard />
                    </DevicesPermissionWrappedProvider>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.SHELL_COMMAND).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='Shell command'
                        className={shellPromptCss.shell_prompt}
                    >
                        <ShellCommandExecution />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.WELCOME_PACKS).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='Welcome pack'
                    >
                        <WelcomePackConfigurationsListing />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.WELCOME_PACK).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='Welcome pack'
                    >
                        <SaveWelcomePack />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.WELCOME_PACK_TRACKING).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title='Welcome Pack Tracking'
                    >
                        <WelcomePackTracking />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.COMMISSIONING).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage permissionCodes={stores.permissionStore.permissionCodes} routes={routeStore.routes}>
                        <CommissioningWizard canAccess={canAccessCommissioning} />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.COMMISSIONING_UPLOAD).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        className={commissioningUploadCss.form}
                    >
                        <CommissioningUpload />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.COMMISSIONING_UPLOAD_REVIEW).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage permissionCodes={stores.permissionStore.permissionCodes} routes={routeStore.routes}>
                        <CommissioningUploadReview />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.FILE_TRANSFER).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        title='File transfer'
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                    >
                        <FileTransfer />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.NEW_FILE_TRANSFER).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        title='New File Transfer'
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                    >
                        <NewFileTransfer />
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.DETAILS).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage permissionCodes={stores.permissionStore.permissionCodes} routes={routeStore.routes}>
                        <DevicesPermissionWrappedProvider
                            permissionCodes={stores.permissionStore.permissionCodes}
                            routes={routeStore.routes}
                            requiredPermissionCodes={['Device Details']}
                        >
                            <VarModalDeviceDetails />
                        </DevicesPermissionWrappedProvider>
                    </PermissionPage>
                }
            />
            <Route
                path={routeStore.getRoute(DEVICES_ROUTE_CODES.DECOMMISSIONING).trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        title='Decommissioning'
                        permissionCodes={stores.permissionStore.permissionCodes}
                        routes={routeStore.routes}
                    >
                        <DecommissioningDevice />
                    </PermissionPage>
                }
            />
            <Route
                path={`${routeStore.getRoute(DEVICES_ROUTE_CODES.DEVICE_GROUPS).trimRootPath(rootRoute.path)}/*`}
                element={
                    <DevicesPermissionWrappedProvider
                        title={'Device groups'}
                        routes={routeStore.routes}
                        permissionCodes={stores.permissionStore.permissionCodes}
                        requiredPermissionCodes={['Device Groups']}
                    >
                        <ManageDeviceGroupsPage />
                    </DevicesPermissionWrappedProvider>
                }
            />
            <Route
                path={`${routeStore.getRoute(DEVICES_ROUTE_CODES.DEVICE_RESET).trimRootPath(rootRoute.path)}/*`}
                element={
                    <PermissionPage
                        title={'Device reset'}
                        routes={routeStore.routes}
                        permissionCodes={stores.permissionStore.permissionCodes}
                    >
                        <DeviceReset />
                    </PermissionPage>
                }
            />
            {Object.keys(EXCLUDE_CODES).map((excludeCode) => (
                <Route
                    key={excludeCode}
                    path={EXCLUDE_CODES[excludeCode]}
                    element={<Error404 message='Page Not Found' />}
                />
            ))}
            <Route path='*' element={<Error404 message='Page Not Found' />} />
        </Routes>
    );
}
